// Assistant - Animated NPC character that guides the player
import * as THREE from 'three';
import gsap from 'gsap';
import { Rendering } from '../Rendering';
import { View } from '../View';
import { CharacterLoader, LoadedCharacter } from '../CharacterLoader';
import { gameState } from '../../game/gameState';

export class Assistant {
    public group: THREE.Group;

    private getAssistantColor(): number {
        const config = gameState.getState().experienceConfig;
        if (config && config.assistantColor) {
            // Convert existing hex string '#RRGGBB' to number 0xRRGGBB
            return parseInt(config.assistantColor.replace('#', ''), 16);
        }
        return 0xFF6B9D; // Default Pink
    }

    private character: LoadedCharacter | null = null;
    private pointingIndicator!: THREE.Group;

    // Fallback procedural character (used while loading)
    private fallbackGroup!: THREE.Group;
    private isModelLoaded: boolean = false;

    constructor() {
        this.group = new THREE.Group();
        this.createFallbackCharacter();
        // this.createSpeechBubble(); // No longer needed for DOM overlay
        this.createPointingIndicator();
        this.loadCharacterModel();
        this.startIdleAnimation();

        // Position at the side of the map
        this.group.position.set(-12, 0, 0);

        Rendering.getInstance().scene.add(this.group);
    }

    private async loadCharacterModel(): Promise<void> {
        try {
            this.character = await CharacterLoader.load('/assets/assistant_idle.glb', {
                'walk': '/assets/assistant_walk.glb',
                'celebrate': '/assets/assistant_dance.glb'
            }, {
                // Character Colors
                'skin': 0xFFD8CC,  // Light/Fair warm skin
                'body': 0xFFD8CC,
                'head': 0xFFD8CC,
                // Outfit
                'top': this.getAssistantColor(),   // Dynamic Pink/Custom
                'shirt': this.getAssistantColor(),
                'pants': 0xFFFFFF, // White pants
                'bottom': 0xFFFFFF,
                'shoes': 0x333333, // Dark shoes
            });

            // Scale and position the model
            this.character.scene.scale.set(1.5, 1.5, 1.5);
            this.character.scene.position.y = 0;

            // Rotate to stand upright - Removed as new models seem correct
            // this.character.scene.rotation.x = -Math.PI / 2;

            // Add to group and remove fallback
            this.group.add(this.character.scene);
            this.fallbackGroup.visible = false;
            this.isModelLoaded = true;

            // Start idle animation if available
            if (this.character.animations.idle) {
                this.character.playAnimation('idle', { loop: true });
            }

            console.log('✅ Assistant character loaded!');
        } catch (error) {
            console.warn('Could not load character model, using fallback:', error);
        }
    }

    private createFallbackCharacter(): void {
        this.fallbackGroup = new THREE.Group();
        // Fallback removed - we will just wait for the model to load
        // to avoid the "capsule man" popping in.
        this.group.add(this.fallbackGroup);
    }

    private createPointingIndicator(): void {
        this.pointingIndicator = new THREE.Group();
        this.pointingIndicator.visible = false;

        // Animated bouncing arrow
        const arrowShape = new THREE.Shape();
        arrowShape.moveTo(0, 0.5);
        arrowShape.lineTo(-0.3, 0);
        arrowShape.lineTo(-0.15, 0);
        arrowShape.lineTo(-0.15, -0.5);
        arrowShape.lineTo(0.15, -0.5);
        arrowShape.lineTo(0.15, 0);
        arrowShape.lineTo(0.3, 0);
        arrowShape.closePath();

        const arrowGeometry = new THREE.ShapeGeometry(arrowShape);
        const arrowMaterial = new THREE.MeshBasicMaterial({
            color: 0xFF6B9D,
            side: THREE.DoubleSide,
        });
        const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
        arrow.rotation.z = Math.PI;
        this.pointingIndicator.add(arrow);

        this.pointingIndicator.position.y = 3;
        Rendering.getInstance().scene.add(this.pointingIndicator);
    }

    public startIdleAnimation(): void {
        // Gentle bobbing animation (for fallback)
        gsap.to(this.fallbackGroup.position, {
            y: 0.2,
            duration: 1.5,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
        });
    }

    // Walk to a position and optionally point
    walkToAndPoint(x: number, z: number, pointAtX?: number, pointAtZ?: number): Promise<void> {
        return new Promise((resolve) => {
            // Rotate to face direction
            const angle = Math.atan2(x - this.group.position.x, z - this.group.position.z);

            // Play walk animation if model is loaded
            if (this.isModelLoaded && this.character?.animations.walk) {
                this.character.playAnimation('walk', { loop: true });
            }

            // Stop idle bobbing during walk
            gsap.killTweensOf(this.fallbackGroup.position);

            // Walking animation timeline
            const tl = gsap.timeline({
                onComplete: () => {
                    // Return to idle animation
                    if (this.isModelLoaded && this.character?.animations.idle) {
                        this.character.playAnimation('idle', { loop: true });
                    } else {
                        this.startIdleAnimation();
                    }
                    resolve();
                }
            });

            // Rotate to face direction
            tl.to(this.group.rotation, {
                y: angle,
                duration: 0.3,
            });

            // Walk with bobbing
            tl.to(this.group.position, {
                x: x,
                z: z,
                duration: 5.0,
                ease: 'power1.inOut',
                onUpdate: () => {
                    // Walking bob for fallback
                    if (!this.isModelLoaded) {
                        this.fallbackGroup.position.y = Math.abs(Math.sin(Date.now() * 0.01)) * 0.2;
                    }
                }
            });

            // If pointing target provided, turn and point
            if (pointAtX !== undefined && pointAtZ !== undefined) {
                const pointAngle = Math.atan2(pointAtX - x, pointAtZ - z);

                tl.to(this.group.rotation, {
                    y: pointAngle,
                    duration: 0.3,
                });

                tl.call(() => this.startPointing(pointAtX, pointAtZ));
            }
        });
    }

    // Start pointing at a location
    startPointing(x: number, z: number): void {
        // Play point animation if available
        if (this.isModelLoaded && this.character?.animations.point) {
            this.character.playAnimation('point', { loop: false });
        }

        // Show pointing indicator at target location
        this.pointingIndicator.position.set(x, 2, z);
        this.pointingIndicator.visible = true;
        this.pointingIndicator.scale.set(0, 0, 0);

        gsap.to(this.pointingIndicator.scale, {
            x: 1,
            y: 1,
            z: 1,
            duration: 0.3,
            ease: 'back.out(1.7)',
        });

        // Bounce animation on indicator
        gsap.to(this.pointingIndicator.position, {
            y: 2.5,
            duration: 0.5,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
        });
    }

    // Stop pointing
    stopPointing(): void {
        // Return to idle
        if (this.isModelLoaded && this.character?.animations.idle) {
            this.character.playAnimation('idle', { loop: true });
        }

        // Hide indicator
        gsap.killTweensOf(this.pointingIndicator.position);
        gsap.to(this.pointingIndicator.scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.2,
            onComplete: () => {
                this.pointingIndicator.visible = false;
            }
        });
    }

    showSpeechBubble(message: string, duration: number = 0): void {
        let bubble = document.getElementById('speech-bubble');
        if (!bubble) {
            bubble = document.createElement('div');
            bubble.id = 'speech-bubble';
            bubble.style.position = 'absolute';
            bubble.style.padding = '12px 24px';
            bubble.style.backgroundColor = 'rgba(255, 249, 230, 0.95)';
            bubble.style.borderRadius = '20px';
            bubble.style.border = '3px solid #FF6B9D';
            bubble.style.color = '#333';
            bubble.style.fontFamily = '"Outfit", sans-serif';
            bubble.style.fontSize = '18px';
            bubble.style.fontWeight = 'bold';
            bubble.style.textAlign = 'center';
            bubble.style.pointerEvents = 'none';
            bubble.style.zIndex = '1000';
            bubble.style.transform = 'translate(-50%, -100%)'; // Center above point
            bubble.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            bubble.style.opacity = '0';
            bubble.style.maxWidth = '90vw'; // Ensure it doesn't overflow screen width
            bubble.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

            // Add content container
            const content = document.createElement('div');
            content.className = 'bubble-content';
            bubble.appendChild(content);

            // Add tail
            const tail = document.createElement('div');
            tail.className = 'bubble-tail'; // Add class for selection
            tail.style.position = 'absolute';
            tail.style.bottom = '-8px';
            tail.style.left = '50%';
            tail.style.transform = 'translateX(-50%) rotate(45deg)';
            tail.style.width = '16px';
            tail.style.height = '16px';
            tail.style.backgroundColor = 'rgba(255, 249, 230, 0.95)';
            tail.style.borderRight = '3px solid #FF6B9D';
            tail.style.borderBottom = '3px solid #FF6B9D';
            bubble.appendChild(tail);

            document.body.appendChild(bubble);
        }

        // Update text safely
        const contentDiv = bubble.querySelector('.bubble-content');
        if (contentDiv) {
            contentDiv.innerHTML = message;
        } else {
            // Fallback for existing structure if any
            const tail = bubble.lastElementChild;
            // Remove all children except tail
            while (bubble.firstChild && bubble.firstChild !== tail) {
                bubble.removeChild(bubble.firstChild);
            }
            if (tail) {
                // Re-add content before tail
                const content = document.createElement('div');
                content.className = 'bubble-content';
                content.innerHTML = message;
                bubble.insertBefore(content, tail);
            } else {
                // Should ideally not happen if created above
                bubble.innerHTML = message;
            }
        }

        // Show
        requestAnimationFrame(() => {
            if (bubble) {
                bubble.style.opacity = '1';
                bubble.style.transform = 'translate(-50%, -110%)'; // Slight move up
            }
        });

        // Hide after duration
        if (duration > 0) {
            setTimeout(() => this.hideSpeechBubble(), duration * 1000);
        }
    }

    hideSpeechBubble(): void {
        const bubble = document.getElementById('speech-bubble');
        if (bubble) {
            bubble.style.opacity = '0';
            bubble.style.transform = 'translate(-50%, -100%)';
        }
    }

    celebrateBuildingPlacement(buildingType: string): void {
        // Stop pointing
        this.stopPointing();

        // Play celebrate animation if available
        if (this.isModelLoaded && this.character?.animations.celebrate) {
            this.character.playAnimation('celebrate', { loop: false });

            // Return to idle after celebrate
            setTimeout(() => {
                if (this.character?.animations.idle) {
                    this.character.playAnimation('idle', { loop: true });
                }
            }, 2000);
        } else {
            // Fallback: Jump animation with GSAP
            gsap.killTweensOf(this.fallbackGroup.position);
            gsap.to(this.fallbackGroup.position, {
                y: 1.5,
                duration: 0.25,
                ease: 'power2.out',
                yoyo: true,
                repeat: 1,
                onComplete: () => this.startIdleAnimation(),
            });
        }

        // Show excited message
        const messages = [
            `Amazing! A ${buildingType}! 💕`,
            `I love it! ✨`,
            `Perfect! 🎉`,
            `Beautiful choice! 💖`,
        ];
        const randomMessage = messages[Math.floor(Math.random() * messages.length)];
        this.showSpeechBubble(randomMessage, 2);
    }

    // Get current position for camera focus
    getPosition(): { x: number; y: number; z: number } {
        return {
            x: this.group.position.x,
            y: this.group.position.y,
            z: this.group.position.z,
        };
    }

    update(delta: number): void {
        // Update character animations
        if (this.character) {
            this.character.update(delta / 1000);
        }

        // Update speech bubble position from 3D to 2D
        const bubble = document.getElementById('speech-bubble');
        if (bubble && bubble.style.opacity !== '0') {
            // Get the absolute world position of the character group
            const tempV = new THREE.Vector3();
            this.group.getWorldPosition(tempV);

            // Add offset for height (above head)
            tempV.y += 2.8;

            // Get camera from View singleton
            const camera = View.getInstance().camera;
            const renderer = Rendering.getInstance().renderer;

            if (camera && renderer) {
                // Project 3D position to Normalized Device Coordinates (-1 to +1)
                tempV.project(camera);

                // Get canvas position relative to viewport
                const rect = renderer.domElement.getBoundingClientRect();

                // Check if we are inside the view
                if (tempV.z < 1) { // z < 1 means it's in front of the camera
                    const idealX = (tempV.x * 0.5 + 0.5) * rect.width + rect.left;
                    const y = (-(tempV.y * 0.5) + 0.5) * rect.height + rect.top;

                    // Clamp X to be within viewport with some padding
                    const padding = 20;
                    const bubbleWidth = bubble.offsetWidth;
                    const minX = padding + bubbleWidth / 2;
                    const maxX = window.innerWidth - padding - bubbleWidth / 2;

                    const clampedX = Math.max(minX, Math.min(maxX, idealX));

                    bubble.style.left = `${clampedX}px`;
                    bubble.style.top = `${y}px`;
                    bubble.style.display = 'block';

                    // Adjust tail position to point to the character
                    // The bubble is shifted by (clampedX - idealX)
                    // So the tail needs to shift by -(clampedX - idealX) relative to the center
                    const tailOffset = idealX - clampedX;
                    const tail = bubble.querySelector('.bubble-tail') as HTMLElement;
                    if (tail) {
                        // Clamp tail to keep it within the bubble's border radius area (approx)
                        const maxTailOffset = bubbleWidth / 2 - 20;
                        const clampedTailOffset = Math.max(-maxTailOffset, Math.min(maxTailOffset, tailOffset));
                        tail.style.transform = `translateX(-50%) translateX(${clampedTailOffset}px) rotate(45deg)`;
                    }

                } else {
                    // Behind camera
                    bubble.style.display = 'none';
                }
            }
        }
    }

    // Make assistant look at the camera
    lookAtCamera(): void {
        const camera = View.getInstance().camera;
        if (!camera) return;

        const cameraPos = camera.position;

        // We want the character to look at the camera horizontally only (Y-axis rotation)
        // Adjust logic: character at (x, z) looks at camera at (cx, cz)
        const targetX = cameraPos.x;
        const targetZ = cameraPos.z;

        this.group.lookAt(targetX, this.group.position.y, targetZ);
    }
}
